/*
 * Copyright © 2023. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useState } from "react";

import Typography from "@mui/material/Typography";
import { apiDelete } from "../../services/apiClient";
import ButtonLoading from "../Button/ButtonLoading";
import { AbstractCase } from "../../types/Entities/AbstractCase";
import { useNavigate } from "react-router";
import DialogModal from "../DialogModal/DialogModal";
import * as messagesActions from "../../store/messages/actions";
import useCheckProductDeletable from "@/src/hooks/useCheckProductDeletable";
import { Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import ProductBox from "@/src/components/ProductBox/ProductBox";

type ServiceWorldCaseDeleteProps = {
  product: AbstractCase;
};

export default function ServiceWorldCaseDelete({ product }: ServiceWorldCaseDeleteProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [confirmCaseDeletionModalOpen, setConfirmCaseDeletionModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data: caseIsDeletable, isLoading } = useCheckProductDeletable(product);

  if (isLoading || !caseIsDeletable) {
    return null;
  }

  async function deleteCase() {
    setIsDeleting(true);
    let wasDeleted;

    try {
      await apiDelete(`/case_deletion/force/${product.id}`);
      wasDeleted = true;
    } catch (error) {
      wasDeleted = false;
    }

    messagesActions.addMessage({
      type: wasDeleted ? "success" : "error",
      text: wasDeleted
        ? "Ihr Fall wurde erfolgreich gelöscht"
        : "Ihr Fall konnte nicht gelöscht werden. Dies kann möglicherweise daran liegen, dass bereits Abrechnungen vorliegen.",
    });

    navigate("/servicewelt");
    setIsDeleting(false);
  }

  return (
    <ProductBox headline={"Fall löschen"}>
      <Grid container spacing={6}>
        <Grid size={{ xs: 12, md: 9 }}>
          <Typography>
            Sie benötigen diesen Fall nicht (mehr) und möchten diesen daher löschen? Dann können Sie dies hier tun. <strong>Wichtig:</strong> Dies kann nicht
            rückgängig gemacht werden und alle mit dem Fall verbundenen Daten und Dokumente werden gelöscht!
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Stack sx={{ height: "100%", justifyContent: "flex-end" }}>
            <ButtonLoading isLoading={isDeleting} variant={"outlined"} color={"warning"} onClick={() => setConfirmCaseDeletionModalOpen(true)}>
              Fall löschen
            </ButtonLoading>
          </Stack>
          <DialogModal
            open={confirmCaseDeletionModalOpen}
            onClose={() => setConfirmCaseDeletionModalOpen(false)}
            title={"Fall löschen"}
            actions={
              <ButtonLoading isLoading={isDeleting} variant={"contained"} color={"warning"} onClick={deleteCase} fullWidth={true}>
                Löschen
              </ButtonLoading>
            }
          >
            <p>Möchten Sie den Fall wirklich löschen?</p>
          </DialogModal>
        </Grid>
      </Grid>
    </ProductBox>
  );
}
